/**
 * OAuth Callback Handler
 * Unified callback page for Google and M365 OAuth flows
 */

import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import GoogleOAuthService from "../services/auth/google-oauth";
import M365OAuthService from "../services/auth/m365-oauth";
import { LoadingScreen } from "../components/LoadingScreen";
import { TokenResponse } from "../types/api";
import { useAuth } from "../contexts/AuthContext";

const EXTENSION_ID = import.meta.env.VITE_EXTENSION_ID;

function tryOpenExtensionSidePanel() {
  const log = (msg: string) => {
    const prev = localStorage.getItem("__sidepanel_debug") || "";
    localStorage.setItem(
      "__sidepanel_debug",
      prev + "\n" + new Date().toISOString() + " " + msg,
    );
  };

  log("[SidePanel] tryOpenExtensionSidePanel called");
  log("[SidePanel] chrome exists: " + (typeof chrome !== "undefined"));
  log(
    "[SidePanel] chrome.runtime exists: " +
      (typeof chrome !== "undefined" && !!chrome.runtime),
  );
  log("[SidePanel] EXTENSION_ID: " + EXTENSION_ID);

  if (typeof chrome !== "undefined" && chrome.runtime && EXTENSION_ID) {
    log("[SidePanel] Sending OPEN_SIDE_PANEL message to extension...");
    chrome.runtime.sendMessage(
      EXTENSION_ID,
      { action: "OPEN_SIDE_PANEL" },
      (response: any) => {
        if (chrome.runtime.lastError) {
          log(
            "[SidePanel] chrome.runtime.lastError: " +
              chrome.runtime.lastError.message,
          );
        } else {
          log("[SidePanel] Extension responded: " + JSON.stringify(response));
        }
      },
    );
  } else {
    log("[SidePanel] SKIPPED — missing chrome/runtime/EXTENSION_ID");
  }
}

export function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false); // Prevent double execution

  useEffect(() => {
    // Prevent duplicate execution in React Strict Mode
    if (hasProcessed.current) {
      console.log("OAuth callback already processed, skipping...");
      return;
    }

    hasProcessed.current = true;
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = async () => {
    try {
      // Parse hash parameters (OAuth returns tokens in URL fragment)
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);

      params.forEach((value, key) => {
        // Hide sensitive tokens (show only first/last 10 chars)
        if (key === "id_token" || key === "access_token") {
          const preview =
            value.length > 20
              ? `${value.substring(0, 10)}...${value.substring(value.length - 10)}`
              : value;
        } else {
          console.log(`  ...`);
        }
      });
      console.log("===========================");

      // Check for OAuth errors
      const oauthError = params.get("error");
      if (oauthError) {
        throw new Error(params.get("error_description") || oauthError);
      }

      // Determine provider from path
      const isGoogle = location.pathname.includes("/auth/google");
      const isM365 = location.pathname.includes("/auth/m365");

      let tokenResponse: TokenResponse;

      if (isGoogle) {
        tokenResponse = await GoogleOAuthService.handleCallback(params);
      } else if (isM365) {
        tokenResponse = await M365OAuthService.handleCallback(params);
      } else {
        throw new Error("Unknown OAuth provider");
      }

      // Track login
      await trackLogin(tokenResponse);

      // Update auth context state with new user data
      await checkAuth();

      // Always try to open the extension side panel after login
      tryOpenExtensionSidePanel();

      // Navigate based on subscription status
      const isSubscribed = sessionStorage.getItem("user_subscribed") === "true";
      if (tokenResponse.isFirstLogin || !isSubscribed) {
        navigate("/pricing", { replace: true });
      } else {
        navigate("/chat", { replace: true });
      }
    } catch (err: any) {
      console.error("OAuth callback error:", err);
      setError(err.message || "Authentication failed");

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    }
  };

  const trackLogin = async (tokenResponse: TokenResponse) => {
    try {
      // Import dynamically to avoid circular dependency
      const { LoginTrackingService } =
        await import("../services/api/login-tracking");
      const provider = tokenResponse.user.provider;
      if (provider === "google" || provider === "m365") {
        await LoginTrackingService.trackLogin({
          provider,
        });
      }
    } catch (error) {
      console.error("Failed to track login:", error);
      // Don't block login flow on tracking failure
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold mb-2">Authentication Failed</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
}
